import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  DataGridPremium,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowClassNameParams
} from "@mui/x-data-grid-premium";
import { useMemo, useRef, useState } from "react";
import {
  parseDelimitedRows,
  type ParsedActivityRow
} from "./catalogModel";
import {
  duplicateTitlesIn,
  parseActivities,
  serializeActivities
} from "./model";
import type { InstrumentDraft } from "./types";

type Props = {
  instrument: InstrumentDraft;
  /** Lowercased trimmed titles of activities already linked to the source
   *  assessment. Rows whose title matches one of these will reuse the
   *  existing DB row at import time rather than creating a new one. */
  reusedTitlesLower?: Set<string>;
  onChange: (next: InstrumentDraft) => void;
};

type View = "paste" | "grid";

export function ActivitiesEditor({
  instrument,
  reusedTitlesLower,
  onChange
}: Props) {
  const [view, setView] = useState<View>("paste");
  const [uploadStatus, setUploadStatus] = useState<
    | { kind: "ok"; message: string }
    | { kind: "error"; message: string }
    | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parsedRows = useMemo<ParsedActivityRow[]>(
    () => parseActivities(instrument),
    [instrument]
  );

  const nonEmptyRows = useMemo(
    () => parsedRows.filter((r) => r.title.trim() || r.description.trim() || r.category.trim()),
    [parsedRows]
  );

  const dups = useMemo(() => duplicateTitlesIn(nonEmptyRows), [nonEmptyRows]);

  const reusedCount = useMemo(() => {
    if (!reusedTitlesLower || reusedTitlesLower.size === 0) return 0;
    let n = 0;
    for (const r of nonEmptyRows) {
      if (reusedTitlesLower.has(r.title.trim().toLowerCase())) n++;
    }
    return n;
  }, [nonEmptyRows, reusedTitlesLower]);

  function commitRows(rows: ParsedActivityRow[]) {
    onChange({ ...instrument, activitiesPasteText: serializeActivities(rows) });
  }

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(file: File) {
    setUploadStatus(null);
    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      setUploadStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Could not read file."
      });
      return;
    }
    // Strip BOM, normalize line endings.
    const normalized = text.replace(/^﻿/, "");
    // Auto-detect separator: tabs win over commas if both appear.
    const firstLine = normalized.split(/\r?\n/, 1)[0] ?? "";
    const sep: "\t" | "," = firstLine.includes("\t") ? "\t" : ",";
    const rawRows = parseDelimitedRows(normalized, sep);
    if (rawRows.length === 0) {
      setUploadStatus({ kind: "error", message: "File appears to be empty." });
      return;
    }
    // Skip a header row when the first cell is literally "title"
    // (case-insensitive). Otherwise treat all rows as data.
    let dataStart = 0;
    const firstCell = (rawRows[0]?.[0] ?? "").trim().toLowerCase();
    if (firstCell === "title") dataStart = 1;
    const incoming: ParsedActivityRow[] = rawRows
      .slice(dataStart)
      .map((cells) => ({
        title: (cells[0] ?? "").trim(),
        description: (cells[1] ?? "").trim(),
        category: (cells[2] ?? "").trim()
      }))
      .filter((r) => r.title || r.description || r.category);
    if (incoming.length === 0) {
      setUploadStatus({
        kind: "error",
        message: "No data rows found. Expected columns: title, description, category."
      });
      return;
    }
    commitRows([...nonEmptyRows, ...incoming]);
    setUploadStatus({
      kind: "ok",
      message: `Added ${incoming.length} ${incoming.length === 1 ? "row" : "rows"} from ${file.name}.`
    });
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <Tabs
          value={view}
          onChange={(_, v) => setView(v as View)}
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
        >
          <Tab value="paste" label="Paste rows" />
          <Tab value="grid" label="Grid" />
        </Tabs>
        <Tooltip title="Upload a CSV/TSV file with columns: title, description, category. Header row optional. Rows are appended to whatever is already here.">
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleUploadClick}
          >
            Upload CSV
          </Button>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFileSelected(file);
            // Reset so selecting the same file twice still triggers onChange.
            e.target.value = "";
          }}
        />
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            {reusedCount > 0 ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`${reusedCount} will reuse`}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            ) : null}
            <Typography
              variant="caption"
              sx={{
                color: nonEmptyRows.length === 0 ? "text.secondary" : "text.primary",
                fontWeight: 500
              }}
            >
              {nonEmptyRows.length} activit{nonEmptyRows.length === 1 ? "y" : "ies"}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      {uploadStatus ? (
        <Alert
          severity={uploadStatus.kind === "ok" ? "success" : "error"}
          variant="outlined"
          onClose={() => setUploadStatus(null)}
        >
          {uploadStatus.message}
        </Alert>
      ) : null}

      {dups.length > 0 ? (
        <Alert severity="error" variant="outlined">
          Duplicate activity name(s): {dups.join(", ")}
        </Alert>
      ) : null}

      {view === "paste" ? (
        <PasteView
          instrument={instrument}
          parsedRows={parsedRows}
          nonEmptyRows={nonEmptyRows}
          onChange={onChange}
          commitRows={commitRows}
          onSwitchToGrid={() => setView("grid")}
        />
      ) : null}

      {view === "grid" ? (
        <GridView
          parsedRows={parsedRows}
          reusedTitlesLower={reusedTitlesLower}
          commitRows={commitRows}
        />
      ) : null}
    </Stack>
  );
}

// -------- Paste view --------
//
// The textarea is a MIRROR of the canonical activities. When the canonical
// rows change (paste, blur-edit, or grid edit in the other tab), the textarea
// re-renders to match. This way, switching away and back never loses content
// — the data lives in the canonical store, not in transient component state.
//
// Multi-line cells (e.g. long descriptions copied from Sheets) are rendered
// with a visible "↵" marker rather than literal quotes, so the user doesn't
// see TSV-quote noise.

const NL_MARKER = "↵";

function serializeForDisplay(
  rows: ParsedActivityRow[],
  sep: "\t" | "," | "|"
): string {
  return rows
    .filter((r) => r.title || r.description || r.category)
    .map((r) =>
      [r.title, r.description, r.category]
        .map((v) => (v ?? "").replace(/\r?\n/g, NL_MARKER))
        .join(sep)
    )
    .join("\n");
}

function parseFromDisplay(
  text: string,
  sep: "\t" | "," | "|"
): ParsedActivityRow[] {
  if (!text.trim()) return [];
  const restoreNewlines = (value: string) =>
    (value ?? "").split(NL_MARKER).join("\n").trim();
  return text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map((line) => {
      const cells = line.split(sep);
      return {
        title: restoreNewlines(cells[0] ?? ""),
        description: restoreNewlines(cells[1] ?? ""),
        category: restoreNewlines(cells[2] ?? "")
      };
    })
    .filter((r) => r.title || r.description || r.category);
}

function PasteView({
  instrument,
  nonEmptyRows,
  onChange,
  commitRows,
  onSwitchToGrid
}: {
  instrument: InstrumentDraft;
  parsedRows: ParsedActivityRow[];
  nonEmptyRows: ParsedActivityRow[];
  onChange: (next: InstrumentDraft) => void;
  commitRows: (rows: ParsedActivityRow[]) => void;
  onSwitchToGrid: () => void;
}) {
  const sepChar: "\t" | "," | "|" =
    instrument.pasteSep === "comma"
      ? ","
      : instrument.pasteSep === "table"
        ? "|"
        : "\t";

  const canonicalDisplay = useMemo(
    () => serializeForDisplay(nonEmptyRows, sepChar),
    [nonEmptyRows, sepChar]
  );

  // While the user is typing, we temporarily drift from canonical so their
  // keystrokes are visible. On blur, we re-parse and write back to canonical.
  const [editing, setEditing] = useState<string | null>(null);
  const display = editing !== null ? editing : canonicalDisplay;

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    // Use the proper TSV/CSV parser — handles quoted multi-line cells from Sheets.
    const incoming = parseDelimitedRows(text, sepChar)
      .map<ParsedActivityRow>((cells) => ({
        title: (cells[0] ?? "").trim(),
        description: (cells[1] ?? "").trim(),
        category: (cells[2] ?? "").trim()
      }))
      .filter((r) => r.title || r.description || r.category);
    if (!incoming.length) return;
    commitRows([...nonEmptyRows, ...incoming]);
    setEditing(null);
  }

  function handleBlur() {
    if (editing === null) return;
    if (editing === canonicalDisplay) {
      setEditing(null);
      return;
    }
    const newRows = parseFromDisplay(editing, sepChar);
    commitRows(newRows);
    setEditing(null);
  }

  function handleSepChange(next: "tab" | "comma" | "table") {
    // Flush any in-progress edit using the OLD separator before switching.
    if (editing !== null && editing !== canonicalDisplay) {
      const newRows = parseFromDisplay(editing, sepChar);
      commitRows(newRows);
      setEditing(null);
    }
    onChange({ ...instrument, pasteSep: next });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Insert a literal tab character on Tab; let Shift+Tab move focus normally
    // so keyboard users can still navigate out.
    if (e.key !== "Tab" || e.shiftKey) return;
    const target = e.target as HTMLTextAreaElement;
    if (typeof target.selectionStart !== "number") return;
    e.preventDefault();
    const start = target.selectionStart;
    const end = target.selectionEnd ?? start;
    const current = target.value;
    const next = current.substring(0, start) + "\t" + current.substring(end);
    setEditing(next);
    // Restore cursor position after React re-renders the textarea.
    requestAnimationFrame(() => {
      target.selectionStart = target.selectionEnd = start + 1;
    });
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        Edit your activities directly here, or paste rows from Excel, Sheets, or CSV. First column = title, second = description, third = category. Press <strong>Tab</strong> to insert a tab between columns; <strong>Shift+Tab</strong> moves focus out. Multi-line content inside a cell is shown as <code>{NL_MARKER}</code> (a visual marker — it isn't part of the data).
      </Typography>
      <TextField
        multiline
        fullWidth
        minRows={4}
        maxRows={14}
        value={display}
        onChange={(e) => setEditing(e.target.value)}
        onPaste={handlePaste}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={
          sepChar === "\t"
            ? "Central line\tShort description\tICU"
            : sepChar === ","
              ? "Central line,Short description,ICU"
              : "Central line|Short description|ICU"
        }
        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
      />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Separator:
        </Typography>
        {(["tab", "comma", "table"] as const).map((sep) => (
          <Button
            key={sep}
            size="small"
            variant={instrument.pasteSep === sep ? "contained" : "outlined"}
            onClick={() => handleSepChange(sep)}
          >
            {sep === "tab" ? "Tab" : sep === "comma" ? "Comma" : "Pipe"}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {nonEmptyRows.length} activit{nonEmptyRows.length === 1 ? "y" : "ies"} ·{" "}
          <Button
            size="small"
            onClick={onSwitchToGrid}
            sx={{ p: 0, minWidth: 0, fontSize: "0.72rem", verticalAlign: "baseline" }}
          >
            View in grid →
          </Button>
        </Typography>
      </Stack>
    </Stack>
  );
}

// -------- Grid view --------

function GridView({
  parsedRows,
  reusedTitlesLower,
  commitRows
}: {
  parsedRows: ParsedActivityRow[];
  reusedTitlesLower?: Set<string>;
  commitRows: (rows: ParsedActivityRow[]) => void;
}) {
  const gridRows = useMemo(
    () =>
      parsedRows.map((row, idx) => ({
        id: idx,
        title: row.title,
        description: row.description,
        category: row.category
      })),
    [parsedRows]
  );

  function getRowClassName(params: GridRowClassNameParams) {
    if (!reusedTitlesLower || reusedTitlesLower.size === 0) return "";
    const titleLower = String(params.row?.title ?? "").trim().toLowerCase();
    return titleLower && reusedTitlesLower.has(titleLower) ? "row-reused" : "";
  }

  const gridColumns = useMemo<GridColDef[]>(
    () => [
      { field: "title", headerName: "Title", flex: 1.2, editable: true, minWidth: 200 },
      { field: "description", headerName: "Description", flex: 1.5, editable: true, minWidth: 240 },
      { field: "category", headerName: "Category", flex: 0.9, editable: true, minWidth: 160 },
      {
        field: "actions",
        headerName: "",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => (
          <Button
            size="small"
            color="error"
            onClick={() => {
              const idx = Number(params.id);
              commitRows(parsedRows.filter((_, i) => i !== idx));
            }}
          >
            Remove
          </Button>
        )
      }
    ],
    [parsedRows, commitRows]
  );

  function applyGridPaste(startRow: number, startCol: number, pastedText: string) {
    const normalized = pastedText.replace(/^﻿/, "");
    const sep = normalized.includes("\t") ? "\t" : ",";
    const rawRows = parseDelimitedRows(normalized, sep);
    if (!rawRows.length) return;
    const next = [...parsedRows];
    rawRows.forEach((cells, ri) => {
      const target = startRow + ri;
      while (next.length <= target) next.push({ title: "", description: "", category: "" });
      cells.forEach((value, ci) => {
        const targetCol = startCol + ci;
        const v = value.trim();
        if (targetCol === 0) next[target]!.title = v;
        else if (targetCol === 1) next[target]!.description = v;
        else if (targetCol === 2) next[target]!.category = v;
      });
    });
    commitRows(next);
  }

  return (
    <Stack spacing={1}>
      <Paper
        variant="outlined"
        sx={{ p: 0, borderRadius: 1 }}
        onPaste={(e) => {
          const text = e.clipboardData.getData("text");
          if (!text) return;
          e.preventDefault();
          applyGridPaste(parsedRows.length, 0, text);
        }}
      >
        <DataGridPremium
          autoHeight
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
          disableRowSelectionOnClick
          rows={gridRows}
          columns={gridColumns}
          getRowClassName={getRowClassName}
          processRowUpdate={(updated) => {
            const idx = Number(updated.id);
            const next = [...parsedRows];
            while (next.length <= idx) next.push({ title: "", description: "", category: "" });
            next[idx] = {
              title: String(updated.title ?? ""),
              description: String(updated.description ?? ""),
              category: String(updated.category ?? "")
            };
            commitRows(next);
            return updated;
          }}
          onProcessRowUpdateError={() => undefined}
          sx={{
            minHeight: 280,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "action.hover",
              borderBottom: 1,
              borderColor: "divider"
            },
            "& .MuiDataGrid-cell": {
              borderBottom: 1,
              borderColor: "divider"
            },
            // Reuse highlight: green left border + faint green wash on the row.
            "& .row-reused": {
              boxShadow: (theme) =>
                `inset 3px 0 0 ${theme.palette.success.main}`,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(76, 175, 80, 0.08)"
                  : "rgba(76, 175, 80, 0.06)"
            },
            "& .row-reused:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(76, 175, 80, 0.14)"
                  : "rgba(76, 175, 80, 0.1)"
            }
          }}
        />
      </Paper>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() =>
            commitRows([...parsedRows, { title: "New activity", description: "", category: "" }])
          }
        >
          Add row
        </Button>
        <Button
          size="small"
          onClick={async () => {
            let text = "";
            try {
              text = await navigator.clipboard.readText();
            } catch {
              const manual = window.prompt(
                "Clipboard read is blocked by the browser. Paste your rows here:",
                ""
              );
              text = manual ?? "";
            }
            if (text.trim()) applyGridPaste(parsedRows.length, 0, text);
          }}
        >
          Paste from clipboard
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="error"
          startIcon={<DeleteSweepIcon />}
          disabled={parsedRows.length === 0}
          onClick={() => {
            if (
              window.confirm(
                "Clear all activities for this instrument? This only affects this instrument."
              )
            ) {
              commitRows([]);
            }
          }}
        >
          Clear all
        </Button>
      </Stack>
    </Stack>
  );
}

